// Copyright 2018 Sorama B.V.

import { RecordingStep, ScanArea, SubScanArea } from "datatypes/ScanStep";
import { Point2D, Point3D } from "datatypes/SoramaLocalApi";
import { URA } from "libraries/Math/URA";
import { ChannelSelection } from "services/ChannelSelection/ChannelSelection";
import { OrderedRecordingSteps } from "./OrderedRecordingSteps";

export class RecordingStepFactory {
    public resolutionToSubScanArea(
        scanArea: ScanArea,
        sensorPositions: Point3D[],
        resolution: number,
        channelSelectionOut: ChannelSelection
    ): SubScanArea {
        if (resolution < 0.001) return undefined;

        let uraProperties = URA.getUraProperties3D(sensorPositions);

        if (!uraProperties.isUra) return undefined; // only URA supported for now

        if (!scanArea) {
            // If no scan area is defined, assume "single shot"
            scanArea = {
                offsetX: 0,
                offsetY: 0,
                offsetZ: 0,
                height: uraProperties.height,
                width: uraProperties.width,
            };
        }

        if (!resolution) resolution = uraProperties.spacing;

        // Determine which mics to use
        const arrayWidth = Math.min(uraProperties.width, scanArea.width);
        const arrayHeight = Math.min(uraProperties.height, scanArea.height);

        const selectedSensorPositions: Point3D[] = [];
        let channelSelection: boolean[] = [];

        // We have a mismatch between selected resolution and the array itself. So use a single microphone.
        if (
            (uraProperties.spacing > resolution && (uraProperties.spacing / resolution) % 1 > 0.00001) ||
            (uraProperties.spacing < resolution && (resolution / uraProperties.spacing) % 1 > 0.00001)
        ) {
            selectedSensorPositions.push({
                x: sensorPositions[0].x,
                y: sensorPositions[0].y,
                z: sensorPositions[0].z,
            });

            channelSelection = new Array(sensorPositions.length).fill(false);
            channelSelection[0] = true;
        } else {
            const epsilon = 0.001; // 1 mm
            const offset: Point3D = {
                x: Math.min(...sensorPositions.map((x) => x.x)),
                y: Math.min(...sensorPositions.map((x) => x.y)),
                z: Math.min(...sensorPositions.map((x) => x.z)),
            };

            for (const pos of sensorPositions) {
                channelSelection.push(false);

                if (pos.x + offset.x > arrayWidth || pos.y + offset.y > arrayHeight) continue;

                const idx = selectedSensorPositions.findIndex((posOther) => {
                    return (
                        Math.abs(pos.x - posOther.x) - resolution < -epsilon &&
                        Math.abs(pos.y - posOther.y) - resolution < -epsilon &&
                        Math.abs(pos.z - posOther.z) - resolution < -epsilon
                    );
                });

                if (idx == -1) {
                    selectedSensorPositions.push(pos);
                    channelSelection[channelSelection.length - 1] = true;
                }
            }
        }

        // Make sure that the properties are up-to-date with the newest mic selection
        channelSelectionOut = new ChannelSelection(channelSelection);
        uraProperties = URA.getUraProperties3D(selectedSensorPositions);

        return <SubScanArea>{
            verticalStep: resolution,
            horizontalStep: resolution,
            numberOfHorizontalSteps: uraProperties.spacing / resolution,
            numberOfVerticalSteps: uraProperties.spacing / resolution,
        };
    }

    public createRecordingSteps(
        scanArea: ScanArea,
        sensorPositions: Point3D[],
        measurementDeviceDimensions: Point2D,
        channelSelection?: ChannelSelection,
        subScanArea?: SubScanArea
    ): RecordingStep[] {
        if (!channelSelection) channelSelection = new ChannelSelection(new Array(sensorPositions.length).fill(true));

        const steps: RecordingStep[] = [];
        let index = 0;

        // Loop over outer dimensions. Note that y is up.
        for (let yOuter = 0; yOuter < scanArea.height; yOuter += measurementDeviceDimensions.y) {
            for (let xOuter = 0; xOuter < scanArea.width; xOuter += measurementDeviceDimensions.x) {
                if (!subScanArea) {
                    const step = new RecordingStep(
                        index++,
                        {
                            X: xOuter,
                            Y: scanArea.height - yOuter - measurementDeviceDimensions.y, // Start top left. Coordinate system is y up.
                            Z: 0,
                        },
                        channelSelection
                    );

                    steps.push(step);
                    continue;
                }

                // Generate for subscanning
                for (let yInner = 0; yInner < subScanArea.numberOfVerticalSteps; yInner++) {
                    for (let xInner = 0; xInner < subScanArea.numberOfHorizontalSteps; xInner++) {
                        const step = new RecordingStep(
                            index++,
                            {
                                X: xOuter + xInner * subScanArea.horizontalStep,
                                Y:
                                    scanArea.height -
                                    yOuter -
                                    yInner * subScanArea.verticalStep -
                                    measurementDeviceDimensions.y,
                                Z: 0,
                            },
                            channelSelection
                        );

                        steps.push(step);
                    }
                }
            }
        }

        return steps;
    }

    public createOrderedRecordingSteps(
        scanArea: ScanArea,
        sensorPositions: Point3D[],
        measurementDeviceDimensions: Point2D,
        channelSelection?: ChannelSelection,
        subScanArea?: SubScanArea
    ): OrderedRecordingSteps {
        return new OrderedRecordingSteps(
            this.createRecordingSteps(
                scanArea,
                sensorPositions,
                measurementDeviceDimensions,
                channelSelection,
                subScanArea
            ),
            scanArea
        );
    }

    public createFromRecordingSteps(recordSteps: RecordingStep[]): OrderedRecordingSteps {
        return new OrderedRecordingSteps(recordSteps);
    }
}
